import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import Container from '@material-ui/core/Container';
import { makeStyles } from '@material-ui/core/styles';
import EmptyResultSetMessage from 'components/@common/EmptyResultSetMessage';
import ClassroomProductLicenseCard, {
  LicenseUpdate
} from 'components/@common/ProductLicenses/ClassroomProductLicenseCard';
import { FormikProps, useFormikContext, FormikValues } from 'formik';
import GET_ORGANIZATION_AVAILABLE_LICENSES from 'graph/organization/get-organization-available-licenses';
import {
  LicenseAction,
  OrganizationLicenseUsage,
  ProductType,
  Student
} from 'types/schema-generated-types';
import { getProductFamilyIds } from 'utilities/product-utils';
import { StudentFormEntry, LicenseAssignFn, LicenseUnassignFn } from '../../../types';
import isValidStudent from '../../../util/is-valid-student';
import StepHeader from '../StepHeader';

interface AssignProductsStepProps {
  index: number;
  organizationId?: number;
  onLicenseAssign: LicenseAssignFn;
  onLicenseUnassign: LicenseUnassignFn;
}

const AssignProductsStep = ({
  index,
  organizationId,
  onLicenseAssign,
  onLicenseUnassign
}: AssignProductsStepProps) => {
  const classes = useStyles();
  const { t } = useTranslation('AssignProductsStep');
  const { values }: FormikProps<FormikValues> = useFormikContext();

  const { data } = useQuery(GET_ORGANIZATION_AVAILABLE_LICENSES, { variables: { organizationId } });

  const students = useMemo(() => {
    // FIXME: Initially renders with full form, then re-renders with subsection of form
    const formStudents: StudentFormEntry[] =
      values?.students ?? values?.ADD_STUDENTS.students ?? [];
    return formStudents.map((student, index) => ({ id: index, ...student })).filter(isValidStudent);
  }, [values]);

  const organizationLicenseUsage: OrganizationLicenseUsage[] =
    data?.organization?.rootOrganization?.licenseUsage ?? [];

  const productFamilyIds = useMemo(
    () =>
      getProductFamilyIds(organizationLicenseUsage, {
        productType: ProductType.STUDENT,
        sortBy: 'product.family.name'
      }),
    [organizationLicenseUsage]
  );

  const assignedProductFamilyStudentCurriculumMap = useMemo(() => {
    // Assigned Product Curriculums by Product Family ID and Student ID
    const result: Map<number, Map<number, number>> = new Map<number, Map<number, number>>();

    students.forEach(student => {
      student.licenses?.forEach(license => {
        const productFamilyMap = result.get(license.productFamilyId) ?? new Map<number, number>();
        if (!!license.productCurriculumId) {
          productFamilyMap.set(student.id, license.productCurriculumId);
        }

        result.set(license.productFamilyId, productFamilyMap);
      });
    });

    return result;
  }, [students]);

  return (
    <div className={classes.root}>
      <StepHeader
        title={`${index}. ${t('Assign Products')}`}
        subtitle={t('You have {{studentCount}} students in your class.', {
          studentCount: students.filter(isValidStudent).length
        })}
        maxWidth="lg"
      />
      {data && (
        <Container maxWidth="lg" className={classes.mainContent}>
          {productFamilyIds.length === 0 && (
            <EmptyResultSetMessage
              style={{ marginTop: '12px' }}
              message={t('No available licenses.', { ns: 'EmptyResultSetMessage' })}
              subtitle={t(`There are no licenses available to assign to students.`, {
                ns: 'EmptyResultSetMessage'
              })}
            />
          )}

          {productFamilyIds.map(productFamilyId => {
            const assignedProductCurriculumMap = assignedProductFamilyStudentCurriculumMap.get(
              productFamilyId
            );

            const pendingUpdates = students.reduce((acc, student) => {
              if (!student.licenses) return acc;

              const updates = student.licenses
                .filter(x => x.productFamilyId === productFamilyId)
                .map(x => ({
                  productFamilyId: x.productFamilyId,
                  productCurriculumId: x.productCurriculumId,
                  studentId: student.id,
                  action: LicenseAction.ASSIGN
                }));

              return [...acc, ...updates];
            }, [] as LicenseUpdate[]);

            return (
              <Container
                key={`card_container_${productFamilyId}`}
                maxWidth="lg"
                className={classes.cardContainer}
              >
                <ClassroomProductLicenseCard
                  assignable
                  productFamilyId={productFamilyId}
                  assignedProductCurriculumMap={assignedProductCurriculumMap}
                  students={(students as unknown) as Student[]}
                  organizationLicenseUsage={organizationLicenseUsage}
                  onLicenseAssign={onLicenseAssign}
                  onLicenseUnassign={onLicenseUnassign}
                  pendingUpdates={pendingUpdates}
                />
              </Container>
            );
          })}
        </Container>
      )}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  cardContainer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  mainContent: {
    marginTop: theme.spacing(2)
  }
}));

export default AssignProductsStep;
